import { type PerspectiveCamera, Plane, Raycaster, Vector2, Vector3 } from 'three';

import type { RotationGizmoManager } from '../rotation-gizmo-manager.class';

/**
 * Snap angle increment: 5 degrees in radians
 */
const SNAP_INCREMENT = (5 * Math.PI) / 180;

/**
 * Snaps an angle to the nearest 5-degree increment
 */
export const snapAngle = (angle: number): number => {
	return Math.round(angle / SNAP_INCREMENT) * SNAP_INCREMENT;
};

export type SetupDragParams = {
	screenX: number;
	screenY: number;
};

/**
 * Fallback plane normal for a given axis (used when ringNormal is unavailable).
 */
const getPlaneNormal = (axis: 'x' | 'y' | 'z'): Vector3 => {
	switch (axis) {
		case 'x': return new Vector3(1, 0, 0);
		case 'y': return new Vector3(0, 1, 0);
		case 'z': return new Vector3(0, 0, 1);
	}
};

/**
 * Projects a screen point onto the rotation plane and returns the angle
 * from the gizmo center. Works with arbitrary plane normals by constructing
 * an orthonormal basis in the plane.
 */
const getPlaneAngle = (
	screenX: number,
	screenY: number,
	domElement: HTMLElement,
	camera: PerspectiveCamera,
	planeNormal: Vector3,
	planeCenter: Vector3
): number | null => {
	const rect = domElement.getBoundingClientRect();
	const pointer = new Vector2(
		((screenX - rect.left) / rect.width) * 2 - 1,
		-((screenY - rect.top) / rect.height) * 2 + 1
	);

	const raycaster = new Raycaster();
	raycaster.setFromCamera(pointer, camera);

	const plane = new Plane().setFromNormalAndCoplanarPoint(planeNormal, planeCenter);
	const intersection = new Vector3();

	if (!raycaster.ray.intersectPlane(plane, intersection)) {
		return null;
	}

	// Vector from center to intersection point in the plane
	const localVec = intersection.sub(planeCenter);

	// Build an orthonormal basis in the plane for consistent atan2.
	// Pick a reference vector not parallel to the normal.
	const ref = Math.abs(planeNormal.y) < 0.9
		? new Vector3(0, 1, 0)
		: new Vector3(1, 0, 0);
	const u = new Vector3().crossVectors(planeNormal, ref).normalize();
	const v = new Vector3().crossVectors(u, planeNormal).normalize();

	return Math.atan2(localVec.dot(v), localVec.dot(u));
};

/**
 * Creates the angle label overlay and appends it next to the canvas.
 */
const createAngleLabel = (domElement: HTMLElement): HTMLDivElement => {
	const label = document.createElement('div');
	label.style.cssText = [
		'position:absolute',
		'pointer-events:none',
		'background:rgba(0,0,0,0.75)',
		'color:#fff',
		'font-family:monospace',
		'font-size:14px',
		'padding:4px 8px',
		'border-radius:4px',
		'white-space:nowrap',
		'z-index:1000',
		'transform:translate(-50%,-150%)'
	].join(';');
	(domElement.parentElement ?? domElement).appendChild(label);
	return label;
};

/**
 * Projects a 3D world point to screen coordinates relative to the canvas parent.
 */
const projectToScreen = (
	worldPoint: Vector3,
	camera: PerspectiveCamera,
	domElement: HTMLElement
): { x: number; y: number } => {
	const projected = worldPoint.clone().project(camera);
	const rect = domElement.getBoundingClientRect();
	const parentRect = (domElement.parentElement ?? domElement).getBoundingClientRect();
	return {
		x: ((projected.x + 1) / 2) * rect.width + (rect.left - parentRect.left),
		y: ((-projected.y + 1) / 2) * rect.height + (rect.top - parentRect.top)
	};
};

/**
 * Begins a drag interaction on a gizmo ring.
 * Records the initial angle and the target object's starting position/rotation
 * so subsequent mouse moves can compute a delta and rotate around the bbox center.
 */
export const beginDragCfn = (self: RotationGizmoManager, params: SetupDragParams): void => {
	const { screenX, screenY } = params;
	const config = self.internalConfig;
	const { domElement, camera } = config;
	const axis = self.internalDragAxis;
	const gizmoGroup = self.internalGizmoGroup;
	const targetObject = self.internalTargetObject;

	if (!axis || !gizmoGroup || !targetObject) return;

	// Use the ring normal computed from the appliance's world transform
	const planeNormal = self.internalRingNormal?.clone() ?? getPlaneNormal(axis);
	const planeCenter = gizmoGroup.position.clone();

	// Snapshot target world position for center-of-mass rotation
	self.internalInitialTargetPosition = targetObject.position.clone();
	const worldPos = new Vector3();
	targetObject.getWorldPosition(worldPos);
	self.internalInitialWorldPosition = worldPos;

	// Compute the ACTUAL world-space rotation axis for the Euler component
	// being modified. This is the object's local axis direction in world space.
	targetObject.updateWorldMatrix(true, false);
	const localAxis = axis === 'z' ? new Vector3(0, 0, 1) : new Vector3(0, 1, 0);
	self.internalWorldRotAxis = localAxis.transformDirection(targetObject.matrixWorld);

	const initialAngle = getPlaneAngle(screenX, screenY, domElement, camera, planeNormal, planeCenter);
	if (initialAngle === null) return;

	self.internalDragStartAngle = initialAngle;
	self.internalAccumulatedAngle = 0;
	self.internalPlaneNormal = planeNormal;
	self.internalPlaneCenter = planeCenter;

	// Create angle label overlay
	const label = createAngleLabel(domElement);
	const screenPos = projectToScreen(planeCenter, camera, domElement);
	label.style.left = `${screenPos.x}px`;
	label.style.top = `${screenPos.y}px`;
	label.textContent = '0°';
	self.internalAngleLabelElement = label;

	// Disable camera controls during drag
	config.onInteractionStart?.();
};

export type UpdateDragParams = {
	screenX: number;
	screenY: number;
};

/**
 * Updates the drag interaction: computes angle delta, snaps, and applies
 * a real-time visual rotation to the target appliance around its bounding
 * box center (not its local origin) to avoid orbiting behaviour.
 */
export const updateDragCfn = (self: RotationGizmoManager, params: UpdateDragParams): void => {
	const { screenX, screenY } = params;
	const config = self.internalConfig;
	const { domElement, camera } = config;
	const planeNormal = self.internalPlaneNormal;
	const planeCenter = self.internalPlaneCenter;
	const dragStartAngle = self.internalDragStartAngle;
	const targetObject = self.internalTargetObject;
	const axis = self.internalDragAxis;
	const initialRotation = self.internalInitialTargetRotation;
	const initialPosition = self.internalInitialTargetPosition;
	const bboxCenter = self.internalBboxCenter;

	if (!planeNormal || !planeCenter || dragStartAngle === null || !targetObject || !axis || !initialRotation || !initialPosition || !bboxCenter) return;

	const currentAngle = getPlaneAngle(screenX, screenY, domElement, camera, planeNormal, planeCenter);
	if (currentAngle === null) return;

	const rawDelta = currentAngle - dragStartAngle;
	const snappedDelta = snapAngle(rawDelta);

	// For top-only (axis='z'), the drag direction is inverted due to the
	// Collada Rx(-PI/2) coordinate flip. Negate the delta to match mouse direction.
	const effectiveDelta = axis === 'z' ? -snappedDelta : snappedDelta;

	// Store the effective (sign-corrected) delta so endDragCfn reports the
	// correct angle to the controller for persistence.
	self.internalAccumulatedAngle = effectiveDelta;

	// Update angle label
	const label = self.internalAngleLabelElement;
	if (label) {
		const degrees = Math.round(effectiveDelta * (180 / Math.PI));
		label.textContent = `${degrees}°`;
	}

	// Update orientation
	targetObject.rotation.set(
		initialRotation.x + (axis === 'x' ? effectiveDelta : 0),
		initialRotation.y + (axis === 'y' ? effectiveDelta : 0),
		initialRotation.z + (axis === 'z' ? effectiveDelta : 0)
	);

	// Orbit position around the bbox center so the model visually spins
	// around its center during drag, not around its front-left origin.
	const initialWorldPos = self.internalInitialWorldPosition;
	const worldRotAxis = self.internalWorldRotAxis;
	if (initialWorldPos && worldRotAxis && bboxCenter) {
		const offset = initialWorldPos.clone().sub(bboxCenter);
		offset.applyAxisAngle(worldRotAxis, effectiveDelta);
		const newWorldPos = bboxCenter.clone().add(offset);
		if (targetObject.parent) {
			targetObject.parent.updateWorldMatrix(true, false);
			targetObject.parent.worldToLocal(newWorldPos);
		}
		targetObject.position.copy(newWorldPos);
	}
};

/**
 * Ends the drag interaction: emits the rotation:complete event
 * and re-enables camera controls.
 */
export const endDragCfn = (self: RotationGizmoManager): void => {
	const config = self.internalConfig;
	const axis = self.internalDragAxis;
	const objectId = self.internalActiveObjectId;
	const accumulatedAngle = self.internalAccumulatedAngle;

	if (axis && objectId && accumulatedAngle !== 0) {
		config.emitEvent('rotation:complete', {
			objectId,
			axis,
			angle: accumulatedAngle
		});
	}

	// Remove angle label
	const label = self.internalAngleLabelElement;
	if (label) {
		label.remove();
		self.internalAngleLabelElement = null;
	}

	// Reset drag state
	self.internalDragAxis = null;
	self.internalDragStartAngle = null;
	self.internalAccumulatedAngle = 0;
	self.internalPlaneNormal = null;
	self.internalPlaneCenter = null;
	self.internalInitialTargetRotation = null;
	self.internalInitialTargetPosition = null;

	// Re-enable camera controls
	config.onInteractionEnd?.();
};
